import {
  IonAlert,
  IonAvatar,
  IonButton as Button,
  IonChip,
  IonContent,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonText,
  useIonActionSheet,
  useIonRouter,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import {
  personCircle,
  searchOutline,
  filter as filterIcon,
  eyeOutline as eyeIcon,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import InfiniteList from '@components/List';
import { APIError } from '@custom-types/APIError';
import RoutePath from '@custom-types/RoutePath';
import { useCheckActionModal } from '@hooks/useCheckActionModal';
import { usePrintBadgeModal } from '@hooks/usePrintBadgeModal';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import { getLatestCheck, Check } from '@pages/Check/service';
import { KioskMode } from '@pages/Kiosk/service';
import { formatTime } from '@services/date-service';
import { generateRoute } from '@services/navigation-service';
import { getCheckOutEnabled, getEventInfo } from '@state/event';
import { getKioskEnabled, getKioskMode } from '@state/kiosk';
import { getSessionInfo } from '@state/session';
import { getLocale } from '@state/system';

import { useKioskPerson, usePeople } from './queries';
import { Person } from './service';
import { PeopleProfileMode } from './types';

const Title = styled.h3`
  font-weight: 700;
  margin-bottom: 20px;
  padding-left: var(--ion-padding, 16px);
  text-align: center;
`;
const SubTitle = styled.p`
  margin-bottom: 10px;
  padding-left: var(--ion-padding, 16px);
`;

const Image = styled(IonImg)`
  width: 40px;
  height: 40px;
`;
const Icon = styled(IonIcon)`
  width: 40px;
  height: 40px;
`;
const ChipIcon = styled(IonIcon)`
  margin-right: 5px;
  font-size: 18px;
`;
const SearchIcon = styled(IonIcon)`
  width: 100px;
  height: 100px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
`;

const Chip = styled(IonChip)`
  margin: 5px 10px 5px 0px;
`;

const ListButtons = styled.div`
  padding-inline: var(--ion-padding, 16px);
  display: flex;
`;
const IonButton = styled(Button)`
  font-size: 14px;
  --padding-start: 10px;
  --padding-end: 10px;
`;
const PeopleCount = styled.p`
  margin-left: auto;
`;

enum SortBy {
  Recent,
  Oldest,
  AZ,
  ZA,
}
const SortByOptions = () => ({
  [SortBy.Recent]: { menuItem: t`Time (Recent)`, chip: t`Recent` },
  [SortBy.Oldest]: { menuItem: t`Time (Oldest)`, chip: t`Oldest` },
  [SortBy.AZ]: { menuItem: t`Name (A-Z)`, chip: t`A-Z` },
  [SortBy.ZA]: { menuItem: t`Name (Z-A)`, chip: t`Z-A` },
});

enum Filters {
  ShowAll,
  CheckedIn,
  CheckedOut,
  NoStatus,
}
const FilterOptions = () => ({
  [Filters.ShowAll]: t`All`,
  [Filters.CheckedIn]: t`Checked In`,
  [Filters.CheckedOut]: t`Checked Out`,
  [Filters.NoStatus]: t`No Status`,
});

const filterChecksByType = (checks: Check[], entity: CheckEntity) =>
  checks.filter(check => check.entityType === entity);

const CheckType = () => ({
  [CheckAction.CheckIn]: t`Checked In`,
  [CheckAction.CheckOut]: t`Checked Out`,
});
enum ErrorCode {
  Invalid = 'invalid_value',
  NotFound = 'person_not_found',
  Default = 'default',
}
const getErrorMessage = (code?: ErrorCode) => {
  const errors = {
    [ErrorCode.Invalid]: t`Not a valid email address.`,
    [ErrorCode.NotFound]: t`No person found for given parameters.`,
    [ErrorCode.Default]: t`We were unable to process your request at this time. Please try again`,
  };

  if (!code) {
    return '';
  }

  return errors[code] || errors[ErrorCode.Default];
};

const getSortBy = (sortBy: SortBy) => {
  switch (sortBy) {
    case SortBy.Recent:
      return '-checkin_at';
    case SortBy.Oldest:
      return 'checkin_at';
    case SortBy.ZA:
      return '-first_name';
    default:
      return 'first_name';
  }
};

const getFilterBy = (filter: Filters) => {
  switch (filter) {
    case Filters.CheckedIn:
      return 'checked_in';
    case Filters.CheckedOut:
      return 'checked_out';
    case Filters.NoStatus:
      return 'not_checked';
    default:
      return 'all';
  }
};

const selector = createSelector(
  getEventInfo,
  getCheckOutEnabled,
  getLocale,
  getKioskMode,
  getKioskEnabled,
  getSessionInfo,
  ({ id: eventId, name: eventName }, isCheckOutEnabled, locale, kioskMode, isKiosk, session) => ({
    eventId,
    eventName,
    isCheckOutEnabled,
    locale,
    kioskMode,
    isKiosk,
    sessionId: session?.id,
    sessionName: session?.name,
  })
);

interface Props {
  mode?: PeopleProfileMode;
}

const List = ({ mode }: Props = { mode: undefined }) => {
  const { push: pushRoute } = useIonRouter();

  const {
    eventId,
    eventName,
    isCheckOutEnabled,
    locale,
    kioskMode,
    isKiosk,
    sessionId,
    sessionName,
  } = useSelector(selector);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(Filters.ShowAll);
  const [sortBy, setSortBy] = useState(SortBy.AZ);

  const [present] = useIonActionSheet();

  const handleSortClick = useCallback(() => {
    present({
      header: t`Sort By`,
      buttons: [
        {
          text: SortByOptions()[SortBy.Recent].menuItem,
          data: SortBy.Recent,
        },
        {
          text: SortByOptions()[SortBy.Oldest].menuItem,
          data: SortBy.Oldest,
        },
        {
          text: SortByOptions()[SortBy.AZ].menuItem,
          data: SortBy.AZ,
        },
        {
          text: SortByOptions()[SortBy.ZA].menuItem,
          data: SortBy.ZA,
        },
        {
          text: t`Cancel`,
          role: 'cancel',
        },
      ],
      onWillDismiss: ({ detail: { data } }) => {
        if (data !== undefined) {
          setSortBy(data);
        }
      },
    });
  }, [present]);

  const handleFilterByClick = useCallback(() => {
    present({
      header: t`View`,
      buttons: [
        {
          text: FilterOptions()[Filters.ShowAll],
          data: Filters.ShowAll,
        },
        {
          text: FilterOptions()[Filters.CheckedIn],
          data: Filters.CheckedIn,
        },
        {
          text: FilterOptions()[Filters.CheckedOut],
          data: Filters.CheckedOut,
        },
        {
          text: FilterOptions()[Filters.NoStatus],
          data: Filters.NoStatus,
        },
        {
          text: t`Cancel`,
          role: 'cancel',
        },
      ],
      onWillDismiss: ({ detail: { data } }) => {
        if (data !== undefined) {
          setFilter(data);
        }
      },
    });
  }, [present]);

  let enablePeopleList = !isKiosk;
  if (sessionId && !isKiosk) {
    // in sessions fetching without search is not allowed
    enablePeopleList = !!search;
  }
  const enableKioskPeopleList = isKiosk && !!search;
  const {
    fetchNextPage,
    hasNextPage,
    isFetching: peopleIsFetching,
    isLoading: peopleIsLoading,
    error: peopleError,
    remove,
    data: allPeoplePages,
  } = usePeople({
    eventId,
    params: {
      search,
      sort: getSortBy(sortBy),
      filter: getFilterBy(filter),
      sessionId,
    },
    // on session page, wait for search to be populated before fetching, otherwise fetch all immediately
    enabled: enablePeopleList,
  });

  const {
    error: kioskError,
    data: kioskPeoplePages,
    isFetching: kioskPeopleIsFetching,
    isLoading: kioskPeopleIsLoading,
  } = useKioskPerson({
    eventId,
    search,
    enabled: enableKioskPeopleList,
  });

  // kiosk condition is needed to be checked, because useQuery returns isLoading as true when enabled is false
  const isKioskPeopleLoading =
    (enableKioskPeopleList && kioskPeopleIsLoading) || kioskPeopleIsFetching;
  const isPeopleLoading = (enablePeopleList && peopleIsLoading) || peopleIsFetching;

  const peoplePages = isKiosk ? kioskPeoplePages : allPeoplePages;

  const entityType = sessionId ? CheckEntity.Session : CheckEntity.Event;

  const errorCode =
    (kioskError as APIError<ErrorCode>)?.errors[0]?.code ||
    (peopleError as APIError<ErrorCode>)?.errors[0]?.code;
  const error = getErrorMessage(errorCode);

  const checkActionProps = {
    title: entityType === CheckEntity.Session ? sessionName! : eventName,
    entityType,
    entityId: sessionId || eventId,
  };

  const { show: showCheckInModal } = useCheckActionModal({
    action: CheckAction.CheckIn,
    goBack: true,
    ...checkActionProps,
  });

  const { show: showCheckOutModal } = useCheckActionModal({
    action: CheckAction.CheckOut,
    goBack: true,
    ...checkActionProps,
  });

  const [showPrintBadgeModal] = usePrintBadgeModal({});

  const presentModal = useCallback(
    (person: Person) => {
      if (!isKiosk) {
        return null;
      }

      if (kioskMode === KioskMode.EventCheckIn || kioskMode === KioskMode.SessionCheckIn) {
        // skipcq: JS-0338
        showCheckInModal(person.checkinCode!);
        return null;
      }
      if (kioskMode === KioskMode.EventCheckOut || kioskMode === KioskMode.SessionCheckOut) {
        showCheckOutModal(person.checkinCode!);
        return null;
      }

      if (
        kioskMode === KioskMode.BadgePrinting ||
        kioskMode === KioskMode.EventCheckInAndBadgePrinting
      ) {
        // skipcq: JS-0338
        showPrintBadgeModal({
          checkInCode: person.checkinCode!,
          shouldCheckIn: kioskMode === KioskMode.EventCheckInAndBadgePrinting,
        });
      }

      return null;
    },
    [isKiosk, kioskMode, showCheckInModal, showCheckOutModal, showPrintBadgeModal]
  );

  useEffect(() => {
    const kioskPeople = kioskPeoplePages?.pages[0]?.data;
    if (kioskPeople?.length === 1) {
      presentModal(kioskPeople[0]);
    }
  }, [kioskPeoplePages?.pages, presentModal, pushRoute]);

  const populateItem = useCallback(
    (person: Person) => {
      if (!eventId) return null;

      const checks = filterChecksByType(person.checkins, entityType);

      const latestCheck = getLatestCheck(
        checks,
        !isCheckOutEnabled ? CheckAction.CheckIn : undefined
      );

      const route = generateRoute(RoutePath.PersonDetails, { personIdOrEmail: person.id, mode });

      return (
        <IonItem
          key={person.id}
          // skipcq: JS-0417
          onClick={() => {
            if (isKiosk) {
              presentModal(person);
              return;
            }

            pushRoute(route);
          }}
        >
          <IonAvatar slot="start">
            {person.profileImage?.thumbnailUrl ? (
              <Image src={person.profileImage.thumbnailUrl} />
            ) : (
              <Icon size="large" icon={personCircle} />
            )}
          </IonAvatar>
          <IonLabel>
            <h2>
              {person.firstName} {person.lastName}
            </h2>
            <IonText color="medium">
              <h5>{person.email}</h5>
            </IonText>
            {latestCheck && (
              <>
                <Chip color={latestCheck.action === CheckAction.CheckIn ? 'success' : 'secondary'}>
                  <IonLabel>{CheckType()[latestCheck.action]}</IonLabel>
                </Chip>
                <IonText color="medium">
                  {formatTime({
                    utcDate: latestCheck.checkinAt,
                    locale,
                    is24HourFormat: false,
                  })}
                </IonText>
              </>
            )}
          </IonLabel>
        </IonItem>
      );
    },
    [entityType, eventId, isCheckOutEnabled, isKiosk, locale, mode, presentModal, pushRoute]
  );

  const getFilterBtnColor = () => {
    if (filter === Filters.CheckedIn || filter === Filters.CheckedOut) {
      return 'primary';
    }
    return 'light';
  };

  if (!eventId) {
    return null;
  }

  const peopleCount = peoplePages?.pages[0]?.meta?.pagination?.totalItemsCount ?? 0;
  const showSortButton = filter === Filters.CheckedIn || filter === Filters.CheckedOut;

  return (
    <IonContent>
      {/* Event and Session uses usePeople hook to fetch people list, which supports search by name and email */}
      {(sessionId || isKiosk) && <SubTitle>{t`Search by Name or Email Address`}</SubTitle>}
      <InfiniteList
        loadData={fetchNextPage}
        endOfList={!hasNextPage}
        onSearch={setSearch}
        isLoading={isKioskPeopleLoading || isPeopleLoading}
        title={!sessionId && !isKiosk ? <Title>{t`People List`}</Title> : null}
        searchType={isKiosk ? 'email' : 'text'}
      >
        {/* show filters only on event page */}
        {!sessionId && !isKiosk && (
          <ListButtons>
            <IonButton shape="round" color={getFilterBtnColor()} onClick={handleFilterByClick}>
              <ChipIcon icon={eyeIcon} />
              {FilterOptions()[filter]}
            </IonButton>
            {showSortButton && (
              <IonButton shape="round" color="light" onClick={handleSortClick}>
                <ChipIcon icon={filterIcon} />
                {SortByOptions()[sortBy].chip}
              </IonButton>
            )}
            <PeopleCount>{t`${peopleCount} People`}</PeopleCount>
          </ListButtons>
        )}
        {peoplePages && (
          <>
            {peoplePages.pages.map(({ data: people }) =>
              people.map(person => populateItem(person))
            )}
            {peoplePages.pages[0].data.length === 0 && (
              <Item>
                <SearchIcon size="large" icon={searchOutline} color="warning" />
                <p>{t`No Results Found`}</p>
              </Item>
            )}
          </>
        )}
      </InfiniteList>
      <IonAlert
        isOpen={!!error}
        onDidDismiss={remove}
        header={t`Error`}
        message={error}
        buttons={[t`OK`]}
      />
    </IonContent>
  );
};

export default List;
